import React from 'react';
import { Box, Checkbox, Divider, InputBase, Radio, Typography } from '@mui/material';
import {
  CalendarMonthOutlined as CalendarIcon, Description as DocumentIcon, PhotoCamera as PhotoIcon,
  PhotoLibrary as GalleryIcon,
  ArrowRight as ArrowIcon,
} from '@mui/icons-material';

/**
 * The pieces of WhatsApp's own flow sheet that the component previews share, drawn to match a
 * real phone (Android) and against Meta's own preview of every component (2026-10-10, the
 * reference flows in flow-core/__fixtures__/parity): a group's options as rows with the title and
 * its description on the left and the control on the right, a rule under the group, inputs as
 * outlined boxes with the label inside, uploads as a description, a limits line and one rounded
 * button, and a pill-shaped Footer.
 *
 * WhatsApp marks every answer nobody has to give "(optional)" by itself: after the label of an
 * input, dropdown, date, checkbox group or upload, and BEFORE the label of an opt-in.
 */

/** A label as WhatsApp prints it: "(optional)" added when the answer is not required. */
export const optionalLabel = (label, required) => (required ? label : `${label} (optional)`);

/**
 * An outlined field with its label inside, like WhatsApp's inputs, dropdowns and dates. With
 * `typeable` it is a real text box, the label its placeholder, because the assistant's preview is
 * there to be tried; dropdowns and dates stay a picture of the box.
 */
export const WaField = ({ label, helper, icon, trailing, tall = false, typeable = false, properties = {} }) => (
  <Box sx={{ mt: 2, opacity: properties.visible === false ? 0.5 : 1 }}>
    <Box
      sx={{
        display: 'flex', alignItems: tall ? 'flex-start' : 'center', gap: 1,
        minHeight: tall ? 96 : 46, px: 1.5, py: tall ? 1.25 : 0,
        border: 1, borderColor: 'grey.500', borderRadius: 2,
        color: properties.enabled === false ? 'text.disabled' : 'text.secondary',
      }}
    >
      {icon}
      {typeable ? (
        <InputBase
          placeholder={label}
          inputProps={{ 'aria-label': label }}
          multiline={tall}
          minRows={tall ? 3 : undefined}
          disabled={properties.enabled === false}
          sx={{ flex: 1, fontSize: 16, '& ::placeholder': { color: 'text.secondary', opacity: 1 } }}
        />
      ) : (
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 16, color: 'inherit' }} noWrap={!tall}>{label}</Typography>
      )}
      {trailing}
    </Box>
    {helper && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5, ml: 1.5 }}>{helper}</Typography>}
  </Box>
);

export const WaCalendarIcon = () => <CalendarIcon sx={{ fontSize: 20 }} />;
export const WaDropdownArrow = () => <ArrowIcon sx={{ fontSize: 20, color: 'text.secondary' }} />;

/**
 * TextBody's markdown, which is standard Markdown and not WhatsApp's chat formatting: Meta's
 * preview turned *this* into italics and left _this_ and ~this~ as typed (2026-10-10).
 */
export const waInlineMarkdown = (text) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|~~[^~]+~~)/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) parts.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith('~~')) parts.push(<s key={match.index}>{token.slice(2, -2)}</s>);
    else parts.push(<em key={match.index}>{token.slice(1, -1)}</em>);
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

export const WA_GREEN = '#1B8F47';

/** A group's question, in the weight WhatsApp sets it. */
export const WaLabel = ({ children }) => (
  <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary', mb: 1 }}>{children}</Typography>
);

/**
 * Radio buttons or checkboxes, the way WhatsApp lays them out: one row per option, the option's
 * description (a duration, a price) under its title, the control at the right edge.
 */
export const WaOptionList = ({ options, control }) => (
  <Box>
    {options.map((option) => (
      <Box key={option.id} sx={{ display: 'flex', alignItems: 'center', py: 1, gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, color: 'text.primary' }}>{option.title}</Typography>
          {option.description && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{option.description}</Typography>
          )}
        </Box>
        {control === 'checkbox'
          ? <Checkbox disabled sx={{ p: 0.5 }} />
          : <Radio disabled sx={{ p: 0.5 }} />}
      </Box>
    ))}
    <Divider sx={{ mt: 1.5 }} />
  </Box>
);

/** WhatsApp marks an upload with no minimum "(optional)" itself, so the preview does too. */
export const uploadLabel = (label, minimum) => (Number(minimum || 0) > 0 ? label : `${label} (optional)`);

/** Accepts the Flow JSON's KB and the builder's bytes; WhatsApp's default is 25 MB. */
const maxSizeMb = (properties) => {
  const kb = Number(properties['max-file-size-kb'])
    || (Number(properties['max-file-size']) ? Number(properties['max-file-size']) / 1024 : 0);
  return kb ? Math.max(1, Math.round(kb / 1024)) : 25;
};

/**
 * The limits line under an upload, worded as Meta's preview words it (2026-10-10), down to the
 * full stop after "Max" that only the required photo version has.
 */
export const uploadLimits = (kind, minimum, maximum, properties) => {
  const min = Number(minimum) || 0;
  const max = Number(maximum) || 1;
  const noun = kind === 'photo' ? 'photo' : 'document';
  const nouns = (n) => `${noun}${n === 1 ? '' : 's'}`;
  const size = maxSizeMb(properties);
  // Exactly as many as required: no count to state, only the size (seen with one photo).
  if (min > 0 && min === max) return `Max file size ${size} MB.`;
  if (min > 0) return `Add ${min} to ${max} ${nouns(max)}. ${kind === 'photo' ? 'Max.' : 'Max'} file size ${size} MB.`;
  return `Add up to ${max} ${nouns(max)}. Max file size ${size} MB.`;
};

/**
 * A DocumentPicker or PhotoPicker: the label, its description, the limits line and a rounded
 * button. Meta's preview offers "Take photo" with a camera for camera and camera_gallery, and
 * "Upload photo" with a gallery icon for gallery only.
 */
export const WaUpload = ({ kind, label, minimum, maximum, description, properties }) => {
  const gallery = kind === 'photo' && properties['photo-source'] === 'gallery';
  const Icon = kind !== 'photo' ? DocumentIcon : gallery ? GalleryIcon : PhotoIcon;
  const action = kind !== 'photo' ? 'Upload document' : gallery ? 'Upload photo' : 'Take photo';
  return (
    <Box sx={{ opacity: properties.visible === false ? 0.5 : 1, mt: 2 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
        {uploadLabel(label, minimum)}
      </Typography>
      {description && <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{description}</Typography>}
      <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: description ? 1 : 0, mb: 1 }}>
        {uploadLimits(kind, minimum, maximum, properties)}
      </Typography>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          py: 1, border: 1, borderColor: 'grey.400', borderRadius: 999,
          color: WA_GREEN, fontSize: 16, fontWeight: 500,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
        {action}
      </Box>
    </Box>
  );
};
